import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowRight } from "lucide-react";

const HERO_IMG = "/assets/truck-black.png";

const LINES = ["EMPOWERING", "THE FUTURE", "OF FREIGHT."];
const EASE = [0.76, 0, 0.24, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const scrollTo = (e, href) => {
  e.preventDefault();
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -64 });
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden pt-32"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
        <img
          src={HERO_IMG}
          alt="ElectroHaul 100% zero emission truck on the highway"
          data-testid="hero-truck-image"
          className="h-[115%] w-full object-cover object-center opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-coal via-coal/75 to-coal/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-coal via-transparent to-coal/70" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 md:px-12">
        <div className="max-w-4xl">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-smoke"
          >
            <span className="inline-block h-2 w-2 animate-pulse-volt bg-volt" />
            EV Freight Systems · India
          </motion.p>

          <h1 className="font-display text-[9vw] font-semibold leading-[1.04] tracking-tight sm:text-4xl lg:text-6xl xl:text-7xl">
            {LINES.map((line, i) => (
              <MaskedLine key={line} delay={0.35 + i * 0.16}>
                {i === 2 ? (
                  <>
                    OF <span className="text-volt">FREIGHT.</span>
                  </>
                ) : (
                  line
                )}
              </MaskedLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-8 max-w-md text-base text-smoke md:text-lg"
          >
            Clean, intelligent, zero-emission trucking for first mile, mid
            mile and long-haul logistics.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              onClick={(e) => scrollTo(e, "#contact")}
              data-testid="hero-cta-quote"
              className="group relative overflow-hidden bg-volt px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black"
            >
              <span className="absolute inset-0 -translate-x-full bg-cyber transition-transform duration-300 ease-out group-hover:translate-x-0" />
              <span className="relative flex items-center gap-2">
                Start Hauling (Contact us)
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </a>
            <a
              href="#company"
              onClick={(e) => scrollTo(e, "#company")}
              data-testid="hero-cta-explore"
              className="group flex items-center gap-2 border border-line px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-volt hover:text-volt"
            >
              The Mission
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
            </a>
          </motion.div>
        </div>

      </div>

      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="absolute bottom-28 right-6 z-10 hidden bg-volt px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-black md:right-12 lg:block"
      >
        100% Zero Emission Fleet
      </motion.span>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="relative z-10 mt-16 border-t border-line"
      >
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 divide-y divide-line sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          {[
            ["100%", "Zero Emission Fleet — Zero Diesel"],
            ["00 g", "Tailpipe CO₂ per Kilometre"],
            ["24×7", "Live Fleet Telemetry"],
          ].map(([v, l]) => (
            <div key={l} className="flex items-baseline gap-4 px-6 py-6 md:px-12">
              <span className="font-mono text-2xl font-bold text-volt">{v}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                {l}
              </span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
